import axios from 'axios';
import { API } from '../utils/config';
import websocketManager from '../utils/websocketManager';

// Ensure axios default auth header is set before making authenticated requests
const ensureAuthHeader = () => {
  const token = getAuthToken();
  if (token && !axios.defaults.headers.common['Authorization']) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('✅ Restored axios auth header from storage');
  }
};

// Get authentication token from localStorage or sessionStorage
// For user requests, prefer user token over admin token
const getAuthToken = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  if (!token) {
    console.error('❌ Auth token not found in storage. User may need to log in again.');
  }

  return token;
};

// Common headers for authenticated requests
const getAuthHeaders = () => {
  const token = getAuthToken();

  // If no token found, return empty headers (will trigger 401)
  if (!token) {
    console.error('❌ Cannot create auth headers: No token available');
    return { headers: {} };
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

/**
 * Vehicle Tracker API Service
 * Handles all API calls for the main dashboard
 */
export const vehicleTrackerService = {
  // Vehicle Management
  async getPublicVehicles() {
    const response = await axios.get(`${API}/vehicles/public`);
    return response.data;
  },

  // Find nearest trucks to a given location (efficient server-side calculation)
  async getNearestTrucksToLocation(lat, lng, startDate = null, endDate = null) {
    let url = `${API}/vehicles/public/nearest-to-location?lat=${lat}&lng=${lng}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    const response = await axios.get(url);
    return response.data;
  },

  // Home location management (for 'Did a truck pass my house?' feature)
  async getHomeLocation() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/auth/me/home-location`, getAuthHeaders());
    return response.data;
  },

  async updateHomeLocation(lat, lng) {
    ensureAuthHeader();
    const response = await axios.put(
      `${API}/auth/me/home-location?lat=${lat}&lng=${lng}`,
      {},
      getAuthHeaders()
    );
    return response.data;
  },

  async deleteHomeLocation() {
    ensureAuthHeader();
    const response = await axios.delete(`${API}/auth/me/home-location`, getAuthHeaders());
    return response.data;
  },

  async getUserVehicles() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/vehicles`, getAuthHeaders());
    return response.data;
  },

  async getSharedVehicles() {
    // const response = await axios.get(`${API}/vehicles/shared-with-me`, getAuthHeaders());
    // New endpoint (complete data with GPS)
    const response = await axios.get(`${API}/vehicles/shared-vehicles`);
    return response.data;
  },

  // Bus Routes
  async getBusRoutes() {
    const response = await axios.get(`${API}/bus-routes`);
    return response.data;
  },

  // Subscriptions
  async getSubscriptions() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/subscriptions`, getAuthHeaders());
    return response.data;
  },

  async getRouteSubscriptions() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/subscriptions`, getAuthHeaders());
    return response.data;
  },

  // Enhanced Subscriptions
  async getEnhancedSubscriptions() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/enhanced-subscriptions`, getAuthHeaders());
    return response.data;
  },

  // Towns data for route information
  async getTowns() {
    const response = await axios.get(`${API}/public/towns?is_active=true`);
    return response.data;
  },

  // Advertisements
  async getActiveAds() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/advertisements/active`, getAuthHeaders());
    return response.data;
  },

  async getUserAds() {
    ensureAuthHeader();
    const response = await axios.get(`${API}/advertisements/my-ads`, getAuthHeaders());
    return response.data;
  },

  async viewAd(adId) {
    ensureAuthHeader();
    const response = await axios.post(`${API}/advertisements/${adId}/view`, {}, getAuthHeaders());
    return response.data;
  },

  async clickAd(adId, adType = 'unknown') {
    ensureAuthHeader();
    const response = await axios.post(`${API}/advertisements/${adId}/click`,
      { ad_type: adType },
      getAuthHeaders()
    );
    return response.data;
  },

  // Location Updates
  async getVehicleLocation(vehicleId) {
    ensureAuthHeader();
    const response = await axios.get(`${API}/locations/vehicle/${vehicleId}`, getAuthHeaders());
    return response.data;
  },

  // WebSocket connection
  // createWebSocketConnection(userId) {
  //   const wsUrl = `${API.replace('http', 'ws').replace('/api', '')}/ws/${userId}`;
  //   return new WebSocket(wsUrl);
  // }

  createWebSocketConnection(userId) {
    // Use the API URL to build the WebSocket URL (replace https with wss, remove /api)
    // Backend expects /ws/{user_id} as a path parameter
    const wsUrl = API.replace('https', 'wss').replace('http', 'ws').replace('/api', '') + `/ws/${userId}`;
    const connectionId = `user_${userId}`;

    return websocketManager.connect(connectionId, wsUrl, userId, {
      autoReconnect: true,
      heartbeat: true
    });
  },

  // Helper methods for WebSocket management
  subscribeToWebSocketEvents(userId, eventType, callback) {
    const connectionId = `user_${userId}`;
    return websocketManager.subscribe(connectionId, eventType, callback);
  },

  unsubscribeFromWebSocketEvents(userId, eventType) {
    const connectionId = `user_${userId}`;
    return websocketManager.unsubscribe(connectionId, eventType);
  },

  disconnectWebSocket(userId) {
    const connectionId = `user_${userId}`;
    return websocketManager.disconnect(connectionId);
  },

  getWebSocketStatus(userId) {
    const connectionId = `user_${userId}`;
    return websocketManager.getConnectionStatus(connectionId);
  }

};



export default vehicleTrackerService;